import type { ReactNode } from 'react';

import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { DEFAULT_THRESHOLDS } from '../../../core/thresholds';
import { ExitDefinitionNote } from '../../../components/ExitDefinitionNote';
import { COLORS, INFO_BOX } from '../../IndividualPlan/tokens';
import {
  ReclassificationPipeline,
  type ReclassificationThresholds,
} from '../../Dashboard/components/ReclassificationPipeline';
import { AssessmentDataAbsentSection } from '../components/AssessmentDataAbsentNote';
import { DateInferredDepartureNote } from '../components/DateInferredDepartureNote';
import { DerivedDepartureNote } from '../components/DerivedDepartureNote';
import { DomainByGradeTable } from '../components/DomainByGradeTable';
import { EnrollmentStatCards } from '../components/EnrollmentStatCards';
import { GradeTable } from '../components/GradeTable';
import { Narrative, unwrapNarrative } from '../components/Narrative';
import { PerDomainCard } from '../components/PerDomainCard';
import { ReclassificationCards } from '../components/ReclassificationCards';
import { ScoreUnitSegment } from '../components/ScoreUnitToggle';
import { EnrollmentBar } from '../components/charts/EnrollmentBar';
import { GradeBars } from '../components/charts/GradeBars';
import { GradeHistoryChart } from '../components/charts/GradeHistoryChart';
import { LtelCountBar } from '../components/charts/LtelCountBar';
import { WidaTrajectoryChart } from '../components/charts/WidaTrajectoryChart';
import { shortYear } from '../utils/drilldownFormat';
import {
  ChartPanel,
  SectionHeading,
  SubsectionCard,
  SubsectionHeader,
} from '../program-evaluation.styled';
import {
  findSection,
  reportSettings,
  reportThresholds,
  type PeCohortBaseline,
  type PeContent,
  type PeDomainOverlayYear,
  type PeDrilldown,
  type PeGradeDetail,
  type PeReport,
  type PeTrajectoryPoint,
  type ScoreUnit,
} from '../types';
import { GRADE_UNKNOWN, gradeShortLabel } from '../listKeys';

export interface ProgramPerformanceProps {
  report: PeReport;
  content: PeContent;
  unit: ScoreUnit;
}

function Subsection({
  number,
  title,
  subtitle,
  tail,
  children,
}: {
  number: string;
  title: string;
  subtitle?: string;
  tail?: ReactNode;
  children: ReactNode;
}) {
  return (
    <SubsectionCard>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <SubsectionHeader>
          <span className="program-performance-subnumber">{number}</span>
          <h3 className="program-performance-subheading">{title}</h3>
        </SubsectionHeader>
        {tail && <Box sx={{ ml: 'auto' }}>{tail}</Box>}
        {subtitle && (
          <Typography
            component="p"
            data-testid="subsection-subtitle"
            sx={{ color: COLORS.muted, fontSize: 16, fontWeight: 500 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </SubsectionCard>
  );
}

const COMPOSITE_METHODOLOGY_NOTE =
  'Comparison frame: last year (district) + trajectory-to-threshold. No WIDA-published expected-growth benchmark exists; see methodology.';

const RESET_YEAR_METHODOLOGY_NOTE =
  'Comparison frame: none this year. WIDA reset the ACCESS scale in 2025-26, so growth comparisons resume once students have a second post-reset administration.';

function CompositeGrowthCallout({
  narrative,
  firstPostResetYear,
}: {
  narrative?: string;
  firstPostResetYear?: boolean;
}) {
  const prose = unwrapNarrative(narrative ?? '');
  if (!prose) {
    return null;
  }

  return (
    <Box
      data-testid="composite-growth-callout"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        bgcolor: INFO_BOX.bg,
        border: `1px solid ${INFO_BOX.border}`,
        borderRadius: '8px',
        p: 2,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: INFO_BOX.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          component="p"
          data-testid="pe-narrative"
          sx={{ color: COLORS.ink, fontSize: 16, lineHeight: 1.5 }}
        >
          {prose}
        </Typography>
        <Typography
          component="p"
          data-testid="composite-growth-methodology"
          sx={{ color: COLORS.muted, fontSize: 16, lineHeight: 1.5 }}
        >
          {firstPostResetYear ? RESET_YEAR_METHODOLOGY_NOTE : COMPOSITE_METHODOLOGY_NOTE}
        </Typography>
      </Box>
    </Box>
  );
}

function reclassificationThresholds(report: PeReport): ReclassificationThresholds | undefined {
  const snapshot = report.metadata.district_config_snapshot;
  const speaking = snapshot?.DomainMinimums?.speaking;
  if (!snapshot || speaking == null) {
    return undefined;
  }
  return { composite: snapshot.CompositeMinimum, speaking };
}

function reclassificationSubtitle(report: PeReport): string | undefined {
  const thresholds = reclassificationThresholds(report);
  if (!thresholds) {
    return undefined;
  }
  return `WIDA single-path · ${thresholds.composite} composite + ${thresholds.speaking} Speaking`;
}

function reclassificationDenominator(report: PeReport): string {
  const state = report.metadata.district_config_snapshot?.State;
  const scope = state ? `${state}'s configuration` : 'the state configuration';
  return `Denominator: students who met all state criteria (composite + domain minimums per ${scope}).`;
}

function ReclassificationNarrativeCallout({
  narrative,
  denominator,
}: {
  narrative?: string;
  denominator: string;
}) {
  const prose = unwrapNarrative(narrative ?? '');
  if (!prose) {
    return null;
  }

  return (
    <Box
      data-testid="reclassification-narrative-callout"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        bgcolor: COLORS.bg,
        border: `1px solid ${COLORS.divider}`,
        borderRadius: '8px',
        p: 2,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: COLORS.navy,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          component="p"
          data-testid="pe-narrative"
          sx={{ color: COLORS.ink, fontSize: 16, lineHeight: 1.5 }}
        >
          {prose}
        </Typography>
        <Typography
          component="p"
          data-testid="reclassification-denominator"
          sx={{ color: COLORS.muted, fontSize: 16, lineHeight: 1.5 }}
        >
          {denominator}
        </Typography>
      </Box>
    </Box>
  );
}

const PIPELINE_AVAILABILITY = {
  bg: '#CCE8FA',
  icon: '#1492E6',
  title: '#072254',
};

const PIPELINE_AVAILABILITY_HEADING = 'Coming after Spring 2027 ACCESS';
const PIPELINE_AVAILABILITY_SUMMARY =
  'Pipeline projection — next year / 1–2 yrs / 3+ yrs / LTEL risk';
const PIPELINE_AVAILABILITY_DETAIL =
  'WIDA reset the ACCESS scale score for 2025–26 and is finalizing new proficiency-level cut scores at the July 2026 Standard Setting event. District-wide pipeline projections (with low / medium / high confidence bands) need a second post-reset administration before they can be generated. This section ships with the post–Spring 2027 release.';

function PipelineAvailabilityCallout() {
  return (
    <Box
      data-testid="pipeline-availability-callout"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        bgcolor: PIPELINE_AVAILABILITY.bg,
        border: `1px solid ${COLORS.divider}`,
        borderRadius: '8px',
        p: 2,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: PIPELINE_AVAILABILITY.icon,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          component="p"
          sx={{
            color: PIPELINE_AVAILABILITY.title,
            fontSize: 16,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
          {PIPELINE_AVAILABILITY_HEADING}
        </Typography>
        <Typography component="p" sx={{ color: COLORS.ink, fontSize: 16, lineHeight: 1.5 }}>
          {PIPELINE_AVAILABILITY_SUMMARY}
        </Typography>
        <Typography component="p" sx={{ color: COLORS.muted, fontSize: 16, lineHeight: 1.5 }}>
          {PIPELINE_AVAILABILITY_DETAIL}
        </Typography>
      </Box>
    </Box>
  );
}

function ltelDefinitionSubtitle(report: PeReport): string {
  const minYears = reportThresholds(report.metadata).ltelMinYears;
  const base = `≥${minYears} years in program`;
  const state = report.metadata.district_config_snapshot?.State ?? '';
  const isUtah = state === '' || /^(ut|utah)$/i.test(state);
  return minYears === DEFAULT_THRESHOLDS.ltelMinYears && isUtah ? `${base} · Utah default` : base;
}

function ltelSection4Note(ltelCohort: PeCohortBaseline): string {
  const clauses = [
    ...(ltelCohort.domain_averages?.length ? ['domain breakdown'] : []),
    ...(typeof ltelCohort.district_grown_count === 'number' &&
    typeof ltelCohort.newly_entered_count === 'number'
      ? ['newly-entered vs. district-grown distinction']
      : []),
  ];
  if (clauses.length === 0) {
    return 'LTEL deep cohort analysis in Section 4.';
  }
  return `LTEL deep cohort analysis — ${clauses.join(', ')} — in Section 4.`;
}

function LtelCallout({
  narrative,
  ltelCohort,
}: {
  narrative?: string;
  ltelCohort: PeCohortBaseline;
}) {
  const prose = unwrapNarrative(narrative ?? '');
  if (!prose) {
    return null;
  }

  return (
    <Box
      data-testid="ltel-callout"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        bgcolor: COLORS.bg,
        border: `1px solid ${COLORS.divider}`,
        borderRadius: '8px',
        p: 2,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: INFO_BOX.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          justifyContent: 'center',
        }}
      >
        <Typography
          component="p"
          data-testid="pe-narrative"
          sx={{ color: COLORS.ink, fontSize: 16, lineHeight: 1.5 }}
        >
          {prose}
        </Typography>
        <Typography
          component="p"
          data-testid="ltel-cohort-note"
          sx={{ color: COLORS.muted, fontSize: 16, lineHeight: 1.5 }}
        >
          {ltelSection4Note(ltelCohort)}
        </Typography>
      </Box>
    </Box>
  );
}

function domainTrajectory(report: PeReport, domain: string): PeTrajectoryPoint[] {
  return report.growth.by_domain[domain]?.trajectory_chart_data ?? [];
}

function thisYearOverlay(report: PeReport, domain: string): PeDomainOverlayYear | undefined {
  if (!report.flashlight.is_integrated) return undefined;
  const years = report.flashlight.by_domain_overlay_data[domain] ?? [];
  return years.find((y) => y.school_year === report.district.school_year);
}

function PairedDomainCard({
  domainLabel,
  report,
  unit,
}: {
  domainLabel: 'Speaking' | 'Writing';
  report: PeReport;
  unit: ScoreUnit;
}) {
  const trajectory = domainTrajectory(report, domainLabel.toLowerCase());
  const overlay = thisYearOverlay(report, domainLabel.toLowerCase());

  return (
    <PerDomainCard
      domainLabel={domainLabel}
      widaTrajectory={trajectory}
      unit={unit}
      flashlightOverlay={overlay}
    />
  );
}

const RESET_GRADE_SUBTITLE =
  'First year on WIDA’s new ACCESS scale: each grade’s level is shown, and growth against earlier years is not. Both comparisons return with the Spring 2027 results. Open a grade for its starting-level groups.';
const GRADE_SUBTITLE =
  'Each grade compared with the same grade last year. Open a grade to see it split by starting level, by cohort, by domain and by school.';
const RESET_GROWTH_NOTE =
  'No growth against last year is shown this year: this is the first year on the new ACCESS scale, and last year’s levels are on the old one. Growth against the same grade last year returns with the Spring 2027 results.';
const SMALL_GRADE_FOOTNOTE =
  'The info marker means a small group: one student’s score can move its average a lot. Small groups don’t produce findings; every row still shows its numbers.';
const DOMAIN_PILOT_FOOTNOTE =
  'Listening and Reading benchmarks are being piloted during the ’26–27 school year.';

const FOOTNOTE_SX = {
  m: 0,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  color: COLORS.muted,
} as const;

export function gradeBarLabel(g: PeGradeDetail): string {
  return gradeShortLabel(g.grade, g.grade_label);
}

function flashlightNote(report: PeReport): string {
  const share = Math.round((report.data_quality.flashlight_coverage_pct ?? 0) * 100);
  return `Flashlight benchmark data isn’t yet available for this district: ${share}% of the students in this report have submissions on file. Within-year Speaking and Writing movement appears here once most students do.`;
}

function priorYearsCaption(report: PeReport): string | undefined {
  const prior = report.reclassification.multi_year_rates.filter(
    (y) => y.school_year !== report.district.school_year,
  );
  if (prior.length === 0) return undefined;
  return `Prior years: ${prior.map((y) => `${shortYear(y.school_year)} · ${y.eligible_count}`).join(' · ')}`;
}

function GradeSections({
  report,
  unit,
  drilldown,
  narrative,
}: {
  report: PeReport;
  unit: ScoreUnit;
  drilldown: PeDrilldown;
  narrative: (element: string) => string | undefined;
}) {
  const settings = reportSettings(report);
  const threshold = settings.smallGroupThreshold;
  const resetYear = report.growth.composite.first_post_reset_year === true;
  const exitThreshold = report.metadata.district_config_snapshot?.CompositeMinimum ?? null;
  const anySmall = drilldown.grades.some((g) => g.small);
  const flashlightThin = (report.data_quality.flashlight_coverage_pct ?? 0) < 0.5;
  return (
    <>
      <Subsection
        number="2.2"
        title="Composite growth by grade"
        subtitle={resetYear ? RESET_GRADE_SUBTITLE : GRADE_SUBTITLE}
        tail={<ScoreUnitSegment unit={unit} />}
      >
        <GradeTable
          grades={drilldown.grades}
          unit={unit}
          resetYear={resetYear}
          threshold={threshold}
        />
        {resetYear && (
          <Typography component="p" data-testid="grade-reset-note" sx={FOOTNOTE_SX}>
            {RESET_GROWTH_NOTE}
          </Typography>
        )}
        {anySmall && (
          <Typography component="p" sx={FOOTNOTE_SX}>
            {SMALL_GRADE_FOOTNOTE}
          </Typography>
        )}
        <GradeHistoryChart history={drilldown.history} unit={unit} threshold={exitThreshold} />
        <CompositeGrowthCallout
          narrative={narrative('composite_growth_narrative')}
          firstPostResetYear={resetYear}
        />
      </Subsection>

      <Subsection number="2.3" title="Per-domain results by grade" subtitle="WIDA this year">
        <DomainByGradeTable grades={drilldown.grades} threshold={threshold} unit={unit} />
        {flashlightThin ? (
          <Box
            data-testid="flashlight-coverage-note"
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              p: 2,
              bgcolor: COLORS.bg,
              borderRadius: '8px',
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 20, color: COLORS.link, flexShrink: 0 }} />
            <Typography
              component="p"
              sx={{ m: 0, fontSize: 16, lineHeight: 1.5, color: COLORS.ink }}
            >
              {flashlightNote(report)}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            <PairedDomainCard domainLabel="Speaking" report={report} unit={unit} />
            <PairedDomainCard domainLabel="Writing" report={report} unit={unit} />
          </Box>
        )}
        <Typography component="p" sx={FOOTNOTE_SX}>
          {DOMAIN_PILOT_FOOTNOTE}
        </Typography>
        <Narrative text={narrative('per_domain_growth_narrative')} />
      </Subsection>
    </>
  );
}

export function ProgramPerformance({ report, content, unit }: ProgramPerformanceProps) {
  const narrative = (element: string) => findSection(content, element)?.text;
  const scoresAbsent = report.assessment_data_absent;
  const derivedDepartures = report.enrollment.departures_this_year?.derived_count ?? 0;
  const dateInferredDepartures = report.enrollment.departures_this_year?.date_inferred_count ?? 0;
  const settings = reportSettings(report);
  const drilldown = report.drilldown ?? undefined;
  const scoredDrilldown = scoresAbsent ? undefined : drilldown;
  const inProgramCount = report.district.in_program_count ?? report.district.el_student_count;
  const ltelFloor = settings.ltelMinYears - 3;

  return (
    <section className="program-performance" data-testid="program-performance">
      <SectionHeading id="program-performance-heading">Program Performance</SectionHeading>

      <Subsection number="2.1" title="Enrollment & Identification">
        <EnrollmentStatCards
          enrollment={report.enrollment}
          reclassification={report.reclassification}
          newcomerMaxYears={settings.newcomerMaxYears}
        />
        <DerivedDepartureNote count={derivedDepartures} color={COLORS.muted} />
        <DateInferredDepartureNote count={dateInferredDepartures} color={COLORS.muted} />
        <ChartPanel>
          <EnrollmentBar years={report.enrollment.multi_year_enrollment} />
        </ChartPanel>
        <Narrative text={narrative('enrollment_narrative')} color={COLORS.ink} />
      </Subsection>

      {scoredDrilldown ? (
        <GradeSections report={report} unit={unit} drilldown={scoredDrilldown} narrative={narrative} />
      ) : (
        <>
          <Subsection
            number="2.2"
            title="Composite Growth"
            subtitle="District-wide average · WIDA ACCESS Composite"
          >
            {scoresAbsent ? (
              <AssessmentDataAbsentSection measure="Composite growth" />
            ) : (
              <>
                <ChartPanel sx={{ p: 3 }}>
                  <WidaTrajectoryChart
                    trajectory={report.growth.composite.trajectory_chart_data}
                    unit={unit}
                  />
                </ChartPanel>
                <CompositeGrowthCallout
                  narrative={narrative('composite_growth_narrative')}
                  firstPostResetYear={report.growth.composite.first_post_reset_year}
                />
              </>
            )}
          </Subsection>

          <Subsection number="2.3" title="Per-Domain Growth">
            {scoresAbsent ? (
              <AssessmentDataAbsentSection measure="Per-domain growth" />
            ) : (
              <>
                <Narrative text={narrative('per_domain_growth_narrative')} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    gap: 2,
                  }}
                >
                  <PairedDomainCard domainLabel="Speaking" report={report} unit={unit} />
                  <PairedDomainCard domainLabel="Writing" report={report} unit={unit} />
                  <PerDomainCard
                    variant="wida-only"
                    domainLabel="Listening"
                    widaTrajectory={domainTrajectory(report, 'listening')}
                    unit={unit}
                  />
                  <PerDomainCard
                    variant="wida-only"
                    domainLabel="Reading"
                    widaTrajectory={domainTrajectory(report, 'reading')}
                    unit={unit}
                  />
                </Box>
              </>
            )}
          </Subsection>
        </>
      )}

      <Subsection number="2.4" title="Exit" subtitle={reclassificationSubtitle(report)}>
        <ExitDefinitionNote />
        <DerivedDepartureNote count={derivedDepartures} color={COLORS.muted} />
        <DateInferredDepartureNote count={dateInferredDepartures} color={COLORS.muted} />
        <ReclassificationCards
          reclassification={report.reclassification}
          preventionMetric={report.ltel.prevention_metric}
          inProgramCount={inProgramCount}
          ltelMinYears={settings.ltelMinYears}
        />
        {scoredDrilldown && (
          <GradeBars
            title="Met exit criteria by grade"
            columns={scoredDrilldown.grades.map((g) => ({
              label: gradeBarLabel(g),
              value: g.met,
            }))}
            caption={priorYearsCaption(report)}
          />
        )}
        <ReclassificationPipeline
          thresholds={reclassificationThresholds(report)}
          variant="outlined"
        />
        <PipelineAvailabilityCallout />
        <ReclassificationNarrativeCallout
          narrative={narrative('reclassification_narrative')}
          denominator={reclassificationDenominator(report)}
        />
      </Subsection>

      <Subsection
        number="2.5"
        title="Long-Term EL Students"
        subtitle={ltelDefinitionSubtitle(report)}
      >
        <ChartPanel>
          <LtelCountBar years={report.ltel.multi_year_counts} />
        </ChartPanel>
        {drilldown && (
          <GradeBars
            title="Long-term ELs by grade"
            columns={drilldown.grades.map((g) => ({
              label: gradeBarLabel(g),
              value: g.grade !== GRADE_UNKNOWN && g.grade <= ltelFloor && g.ltel === 0 ? null : g.ltel,
            }))}
            footnote={`Grades up to ${ltelFloor} show n/a rather than zero: a student can’t reach ${settings.ltelMinYears} years in the program by then.`}
          />
        )}
        <LtelCallout narrative={narrative('ltel_narrative')} ltelCohort={report.subgroups.ltel} />
      </Subsection>
    </section>
  );
}
